import React, { useMemo } from 'react'
import classnames from 'classnames/bind'
import _ from 'lodash'
import * as graph from '@adc/sdk'
// @ts-ignore no types
import type {
  CurrentTargetAudience,
  Listing,
  Maybe,
  OnlineAuctionSegment,
  OnlineAuctionSegmentBidRank,
} from '@adc/sdk/types'
import WhatChangedSummarySection from './whatChangedSummarySection'
import { formatters, traditionalOffer } from '../../../../utils'
import styles from './styles.scss'
import { bidding } from '@adc/blueprint/'

const cx = classnames.bind(styles)

const ELM_IDS = {
  offerDetails: 'offer-details',
  closing: 'closing',
  contingencies: 'contingencies',
}

type SubsectionChild = {
  label: string
  value: string | number
  isMoneyValue?: boolean
  hide?: boolean
  elmId?: string
  previousValue?: string | number
  previousElmId?: string
}

type Section = {
  title?: string
  children: SubsectionChild[]
  className?: string
}

const valueOrDefault = (
  value: string | number | undefined | Maybe<number>,
  asNegative = false,
  defaultValue = 'N/A'
) => {
  return value !== undefined && value !== null
    ? asNegative
      ? `-${value}`
      : value
    : defaultValue
}
const fields = bidding.enums.WhatChangedFields
interface WhatChangedSummaryProps {
  bidRank: OnlineAuctionSegmentBidRank
  viewingFrom: CurrentTargetAudience
  listing?: Listing
  segment?: OnlineAuctionSegment
  previousValues: {
    [key: string]: string | number
  }
}

const WhatChangedSummary = ({
  bidRank,
  viewingFrom = graph.enums.CurrentTargetAudience.Seller,
  listing,
  segment,
  previousValues,
}: WhatChangedSummaryProps) => {
  const terms = bidRank.bid?.terms
  const {
    buyerCommissionsAmount,
    sellerCommissionsAmount,
    sellerConcessionAmount,
  } = traditionalOffer.getOfferTermsCalculations(bidRank.bid)

  let commissionPayerType = graph.enums.CommissionPayerType.Seller
  if (bidRank?.bid?.terms?.commission?.length) {
    commissionPayerType = bidRank?.bid?.terms?.commission[0].paid_by
  }

  const paymentOption = _.find(
    traditionalOffer.paymentTypeOptions,
    option => option.value === terms?.payment_type
  ) as { text: string } | undefined

  const previousPaymentOption = _.find(
    traditionalOffer.paymentTypeOptions,
    option => option.value === previousValues['/terms/payment_type']
  ) as { text: string } | undefined

  let sections = useMemo<Section[]>(
    () => [
      {
        title: 'Offer Details',
        className: cx('row-children'),
        children: [
          {
            label: 'Purchase Price',
            value: valueOrDefault(terms?.offer_amount),
            isMoneyValue: !!terms?.offer_amount,
            elmId: `${ELM_IDS.offerDetails}-purchase-price`,
            previousValue: previousValues[fields.offerAmount],
          },
          {
            label: 'Payment Type',
            value: valueOrDefault(paymentOption?.text, false, 'N/A'),
            elmId: `${ELM_IDS.offerDetails}-payment-type`,
            previousValue: previousPaymentOption?.text,
          },
          {
            label: 'Down payment',
            value: valueOrDefault(terms?.down_payment_amount, false, `0`),
            elmId: `${ELM_IDS.offerDetails}-down-payment`,
            isMoneyValue: true,
            previousValue: previousValues[fields.downPaymentAmount],
          },
          {
            label: 'Earnest Money Deposit',
            value: valueOrDefault(
              terms?.earnest_money_deposit_amount,
              false,
              `0`
            ),
            elmId: `${ELM_IDS.offerDetails}-earnest-money-deposit`,
            isMoneyValue: true,
            previousValue: previousValues[fields.earnestMoneyDepositAmount],
          },
        ],
      },
      {
        title: 'Closing',
        className: cx('row-children'),
        children: [
          {
            label: 'Closing Date',
            value: valueOrDefault(
              terms?.close_escrow_duration
                ? `${terms?.close_escrow_duration} Days from Contract Execution`
                : undefined
            ),
            elmId: `${ELM_IDS.closing}-closing-date`,
            previousValue: previousValues[fields.closeEscrowDuration]
              ? `${
                  previousValues[fields.closeEscrowDuration]
                } Days from Contract Execution`
              : undefined,
          },
          {
            label: 'Rentback',
            value: valueOrDefault(
              terms?.leaseback_duration
                ? `${formatters.formatMoney(
                    terms?.leaseback_amount
                  )} per day for ${terms?.leaseback_duration} Days`
                : undefined,
              false,
              'Not Offered'
            ),
            elmId: `${ELM_IDS.closing}-rentback`,
            hide:
              !listing?.seller_terms?.leaseback_terms
                ?.leaseback_period_in_days &&
              !listing?.seller_terms?.leaseback_terms?.leaseback_period_rent,
            previousValue: previousValues[fields.leasebackDuration]
              ? `${formatters.formatMoney(
                  previousValues[fields.leasebackAmount]
                )} per day for ${previousValues[fields.leasebackDuration]} Days`
              : undefined,
          },
          {
            label: 'Seller Paid Closing Costs',
            value: valueOrDefault(sellerConcessionAmount, false, `0`),
            elmId: `${ELM_IDS.closing}-seller-paid-closing-costs`,
            isMoneyValue: true,
            previousValue: previousValues[fields.sellerConcessionAmount],
          },
          {
            label: 'Buyer’s Agent Commission',
            value: valueOrDefault(
              buyerCommissionsAmount + sellerCommissionsAmount,
              false,
              `0`
            ),
            elmId: `${ELM_IDS.closing}-appraisal-contingency`,
            isMoneyValue: true,
            previousValue: previousValues[fields.agentCommissionAmount],
          },
          {
            label: 'BA Commission Paid By',
            value: commissionPayerType,
            elmId: `${ELM_IDS.closing}-appraisal-contingency`,
            previousValue: previousValues[fields.paidBy],
          },
        ],
      },
      {
        title: 'Contingencies',
        className: cx('row-children'),
        children: [
          {
            label: 'Option Period',
            value: valueOrDefault(
              terms?.option_period_duration
                ? `${
                    formatters.formatMoney(
                      terms?.option_period_cost_amount
                    ) as string
                  } for ${terms?.option_period_duration} Days`
                : undefined,
              false,
              `Not Requested`
            ),
            elmId: `${ELM_IDS.contingencies}-option-period`,
            previousValue: previousValues[fields.optionPeriodDuration]
              ? `${
                  formatters.formatMoney(
                    previousValues[fields.optionPeriodCostAmount]
                  ) as string
                } for ${previousValues[fields.optionPeriodDuration]} Days`
              : undefined,
          },
          {
            label: 'Financing Contingency',
            value: valueOrDefault(
              terms?.financing_contingency_duration
                ? `${terms?.financing_contingency_duration} Days`
                : undefined,
              false,
              'Not Requested'
            ),
            elmId: `${ELM_IDS.contingencies}-financing-contingency`,
            previousValue: previousValues[fields.financingContingencyDuration]
              ? `${previousValues[fields.financingContingencyDuration]} Days`
              : undefined,
          },
          {
            label: 'Appraisal Contingency',
            value: valueOrDefault(
              terms?.appraisal_contingency_duration
                ? `${terms?.appraisal_contingency_duration} Days`
                : undefined,
              false,
              `Not Requested`
            ),
            elmId: `${ELM_IDS.contingencies}-appraisal-contingency`,
            previousValue: previousValues[fields.appraisalContingencyDuration]
              ? `${previousValues[fields.appraisalContingencyDuration]} Days`
              : undefined,
          },
        ],
      },
    ],
    [bidRank, listing, previousValues, segment, viewingFrom]
  )

  let additionalNotes = useMemo<Section>(() => {
    return {
      title: 'Additional Notes',
      className: cx('row-children'),
      children: [
        {
          label: '',
          value: terms?.notes?.additional_notes || '',
          elmId: `additional-notes-field`,
          previousValue: previousValues[fields.additionalNotes],
        },
      ],
    }
  }, [bidRank, listing, segment, viewingFrom])
  return (
    <div className={cx('section-container')}>
      <div key='left' className={cx('summary-section-container')}>
        <WhatChangedSummarySection
          sections={sections as any}
          additionalNotes={additionalNotes as any}
        />
      </div>
    </div>
  )
}

export default WhatChangedSummary
